/**
 * LiveTestingContainer
 *
 * Live Testing Preview를 위한 Container (unstated-next)
 * 선택된 QuestionTree 정보를 주입받아 질문 데이터와 답변 상태를 관리
 */

import { useCallback, useMemo } from 'react';
import { createContainer } from 'unstated-next';
import { useQueries } from '@tanstack/react-query';
import { intersection, isEmpty, toNumber } from 'lodash';
import { useLiveTestingData } from './useLiveTestingData';
import { useLiveTestingAnswers } from './useLiveTestingAnswers';
import { usePhoneVerification } from './usePhoneVerification';
import { useBodyPartTranslations } from './useBodyPartTranslations';
import { useEqVasTranslations } from './useEqVasTranslations';
import { useRangeTranslations } from './useRangeTranslations';
import {
  getQuestionControllerFindByTreeIdQueryOptions,
  getQuestionControllerGetBodySelectTreeConfigQueryOptions,
  getQuestionControllerFindByIdQueryOptions,
} from '../../../../api/generated/questions/questions';
import { getQuestionTreeControllerFindByTreeKeyQueryOptions } from '../../../../api/generated/question-trees/question-trees';
import { usePatientVisitControllerSuggestSymptoms } from '../../../../api/generated/patient-visits/patient-visits';
import type {
  BodySelectTreeConfigResponseDto,
  QuestionDetailResponseDto,
  QuestionResponseDto,
  ShowConditionDto,
} from '../../../../api/generated/types';
import type { V2QsStage } from '../../../../shared/components/question-types/V2QsStageMileMarker';
import type { LiveTestingAnswerValue } from './types';
import {
  THIRD_PARTY_OPTION_MYSELF,
  THIRD_PARTY_OPTION_SOMEONE_ELSE,
  THIRD_PARTY_QUESTION_KEY,
} from '../../../../../constants/third-party-question';

export interface LiveTestingContainerState {
  selectedTreeId?: number | null;
  selectedTreeKey?: string;
  title?: string;
  description?: string;
  showHeader?: boolean;
  locale?: string;
  visitUuid?: string;
  visitSex?: string; // 백엔드에서 가져온 성별 (o-male, o-female)
  surveyId?: string | number;
  organizationId?: string | number;
  translationTreeKeys?: string[];
  stageMarkerStages?: V2QsStage[];
  isContextLoading?: boolean;
  emptySelectionLabel?: string;
  emptySelectionDescription?: string;
  emptyQuestionsLabel?: string;
  emptyQuestionsDescription?: string;
  /** 이미 선택된 증상 트리 키들 (sx_collect_associated용) */
  selectedSymptomTreeKeys?: string[];
  /** 질문 스킵 시 호출되는 콜백 (sx_collect_associated 등에서 사용) */
  onSkipQuestion?: () => void;
  /** optionConditions 계산용 리포트 상태 (V1 parity) */
  reportState?: unknown;
  /** optionConditions 계산용 설문 데이터 (V1 parity) */
  survey?: unknown;
  /** 비활성 질문 필터링 (환자 플로우에서 true) */
  enabledOnly?: boolean;
}

const LiveTestingContainer = createContainer((initialState: LiveTestingContainerState = {}) => {
  const {
    selectedTreeId,
    selectedTreeKey,
    title = '',
    description = '',
    showHeader = true,
    locale,
    visitUuid,
    visitSex,
    surveyId,
    organizationId,
    translationTreeKeys = [],
    stageMarkerStages = [],
    isContextLoading = false,
    emptySelectionLabel = 'Select an item from the left sidebar to preview',
    emptySelectionDescription = '',
    emptyQuestionsLabel = 'No questions to preview',
    emptyQuestionsDescription = '',
    selectedSymptomTreeKeys = [],
    onSkipQuestion,
    reportState,
    survey,
    enabledOnly = false,
  } = initialState;

  const currentLocale = locale || 'english';

  // 질문 데이터 로드
  const {
    questions: allQuestions,
    isLoading: isQuestionsLoading,
    isError,
    error,
  } = useLiveTestingData({
    treeId: selectedTreeId,
    locale: currentLocale,
    enabledOnly,
  });

  const questionDetailQueries = useQueries({
    queries: allQuestions.map((question) => {
      const queryOptions = getQuestionControllerFindByIdQueryOptions(question.id);
      return {
        ...queryOptions,
        enabled: Boolean(question.id),
      };
    }),
  });

  const questionDetailsMap = useMemo(() => {
    const map = new Map<number, QuestionDetailResponseDto>();
    allQuestions.forEach((question, index) => {
      const detail = questionDetailQueries[index]?.data as QuestionDetailResponseDto | undefined;
      if (detail) {
        map.set(question.id, detail);
      }
    });
    return map;
  }, [questionDetailQueries, allQuestions]);

  const filterEligibleConditions = useCallback(
    (showConditions?: ShowConditionDto[] | null) => {
      if (!selectedTreeKey) return showConditions || [];
      const filtered = showConditions?.filter((condition) => {
        const { targetTreeKeys, targetTreeKey, func, qsKey } = condition;

        // Helper function conditions should apply across trees.
        if (
          func === 'isThirdParty' ||
          func === 'isPatientSelf' ||
          qsKey === THIRD_PARTY_QUESTION_KEY
        ) {
          return true;
        }

        if (!isEmpty(targetTreeKeys) && !targetTreeKeys?.includes(selectedTreeKey || '')) {
          return false;
        }
        if (targetTreeKey && targetTreeKey !== selectedTreeKey) {
          return false;
        }
        return true;
      });
      return filtered || [];
    },
    [selectedTreeKey],
  );

  const extractAnswerKeys = useCallback((value?: LiveTestingAnswerValue): string[] => {
    if (value == null) return [];
    if (typeof value === 'string') return [value];
    if (typeof value === 'number') return [String(value)];
    if (Array.isArray(value)) return value.map((item) => String(item));
    if (typeof value === 'object' && 'selectedKeys' in value) {
      return (value.selectedKeys as string[]).map((item) => String(item));
    }
    if (typeof value === 'object' && 'answerKeys' in (value as Record<string, unknown>)) {
      return ((value as { answerKeys?: string[] }).answerKeys || []).map((item) => String(item));
    }
    if (typeof value === 'object' && 'answerValues' in (value as Record<string, unknown>)) {
      return ((value as { answerValues?: Array<string | number> }).answerValues || []).map((item) => String(item));
    }
    return [];
  }, []);

  const extractNumericValue = useCallback((value?: LiveTestingAnswerValue): number | null => {
    if (value == null) return null;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const parsed = toNumber(value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    if (Array.isArray(value) && value.length > 0) {
      const parsed = toNumber(value[0]);
      return Number.isFinite(parsed) ? parsed : null;
    }
    if (typeof value === 'object' && 'selectedKeys' in value) {
      const firstKey = (value.selectedKeys as string[])[0];
      const parsed = toNumber(firstKey);
      return Number.isFinite(parsed) ? parsed : null;
    }
    if (typeof value === 'object' && 'answerValues' in (value as Record<string, unknown>)) {
      const firstValue = (value as { answerValues?: Array<string | number> }).answerValues?.[0];
      const parsed = toNumber(firstValue);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  }, []);

  // 답변 상태 관리
  const { answerMap, updateAnswer, resetAnswers, getAnswer, hasAnswer } =
    useLiveTestingAnswers({
      treeKey: selectedTreeKey,
      resetOnTreeChange: false,
    });
  const suggestSymptomsMutation = usePatientVisitControllerSuggestSymptoms();

  const suggestSymptoms = useCallback(
    async (uuid: string) =>
      suggestSymptomsMutation.mutateAsync({
        data: { uuid },
      } as any),
    [suggestSymptomsMutation],
  );

  // 성별 판단 (V1 useGlobalPaState와 동일한 로직)
  // 우선순위:
  // 1. visitSex (백엔드에서 가져온 성별)
  // 2. q-patient-sex 질문의 답변
  // 3. 모든 답변에서 o-female/o-male 값 검색
  const isMale = useMemo(() => {
    const FEMALE_KEY = 'o-female';
    const MALE_KEY = 'o-male';
    const SEX_QUESTION_KEY = 'q-patient-sex';

    // 방법 1: visitSex (백엔드에서 가져온 성별) 확인
    if (visitSex) {
      return visitSex !== FEMALE_KEY;
    }

    // 방법 2: q-patient-sex 질문 직접 확인
    const sexAnswer = answerMap[SEX_QUESTION_KEY];
    if (sexAnswer) {
      const answerKeys = extractAnswerKeys(sexAnswer);
      return !answerKeys.includes(FEMALE_KEY);
    }

    // 방법 3: 모든 답변에서 o-female/o-male 값 검색
    const hasFemaleAnswer = Object.values(answerMap).some((value) => {
      const keys = extractAnswerKeys(value);
      return keys.includes(FEMALE_KEY);
    });
    if (hasFemaleAnswer) {
      return false;
    }

    const hasMaleAnswer = Object.values(answerMap).some((value) => {
      const keys = extractAnswerKeys(value);
      return keys.includes(MALE_KEY);
    });
    if (hasMaleAnswer) {
      return true;
    }

    return true; // 기본값: 남성
  }, [visitSex, answerMap, extractAnswerKeys]);

  // 선택된 증상 트리 키 계산 (answerMap에서 추출 + 초기값)
  // V1의 selectedSxTrees 로직과 동일
  const computedSelectedSymptomTreeKeys = useMemo(() => {
    const keysFromAnswerMap: string[] = [];

    // answerMap에서 sx- 로 시작하는 키 추출
    Object.values(answerMap).forEach((answer) => {
      const answerKeys = extractAnswerKeys(answer);
      answerKeys.forEach((key) => {
        if (key && key.startsWith('sx-')) {
          keysFromAnswerMap.push(key);
        }
      });
    });

    // 초기값과 합치기
    const allKeys = [...(selectedSymptomTreeKeys || []), ...keysFromAnswerMap];
    // Only symptom tree keys are valid for associated-symptom tree lookups.
    const symptomTreeKeys = allKeys.filter((key) => key && key.startsWith('sx-'));
    return Array.from(new Set(symptomTreeKeys));
  }, [answerMap, extractAnswerKeys, selectedSymptomTreeKeys]);

  const isQuestionVisible = useCallback(
    (question: QuestionResponseDto) => {
      if (!question.enabled) return false;

      const detail = questionDetailsMap.get(question.id);
      const showConditions =
        ((question as any).showConditions as ShowConditionDto[] | undefined | null) ||
        (detail?.showConditions as ShowConditionDto[] | undefined | null);

      if (!showConditions || showConditions.length === 0) {
        if (question.hasConditions) {
          return false;
        }
        return true;
      }

      const eligibleConditions = filterEligibleConditions(showConditions);
      if (eligibleConditions.length === 0) {
        return true;
      }

      const evaluateCondition = (condition: ShowConditionDto) => {
        const { operator, qsKey, value, keys, type, category, uiMetadata } = condition;
        const answerValue = qsKey ? answerMap[qsKey] : undefined;

        if (uiMetadata && answerValue && typeof answerValue === 'object' && 'uiMetadata' in answerValue) {
          const targetMeta = uiMetadata as Record<string, string>;
          const answerMeta = (answerValue as { uiMetadata?: Record<string, string> }).uiMetadata || {};
          const allMatch = Object.keys(targetMeta).every((key) => targetMeta[key] === answerMeta[key]);
          if (allMatch) {
            return true;
          }
        }

        if (operator) {
          const lhs = extractNumericValue(answerValue);
          const rhs = toNumber(value);
          if (lhs !== null && Number.isFinite(lhs) && Number.isFinite(rhs)) {
            if (operator === 'lte' && lhs <= rhs) return true;
            if (operator === 'lt' && lhs < rhs) return true;
            if (operator === 'gte' && lhs >= rhs) return true;
            if (operator === 'gt' && lhs > rhs) return true;
            if (operator === 'eq' && lhs === rhs) return true;
            if (operator === 'ne' && lhs !== rhs) return true;
            return false;
          }

          const rhsStr = value ?? '';
          const answerKeys = extractAnswerKeys(answerValue);
          if (operator === 'eq') {
            return answerKeys.some((item) => item === rhsStr);
          }
          if (operator === 'ne') {
            return answerKeys.every((item) => item !== rhsStr);
          }
        }

        if (type === 'include' || (keys && keys.length > 0)) {
          const answerKeys = extractAnswerKeys(answerValue);
          return !isEmpty(intersection(answerKeys, keys || []));
        }

        if (condition.func) {
          const thirdPartyAnswerKeys = (() => {
            const direct = extractAnswerKeys(answerMap[THIRD_PARTY_QUESTION_KEY]);
            if (direct.length > 0) return direct;

            const match = Object.values(answerMap).find((answerValueItem) => {
              const answerValueKeys = extractAnswerKeys(answerValueItem);
              return (
                answerValueKeys.includes(THIRD_PARTY_OPTION_MYSELF) ||
                answerValueKeys.includes(THIRD_PARTY_OPTION_SOMEONE_ELSE)
              );
            });

            return match ? extractAnswerKeys(match) : [];
          })();
          if (condition.func === 'isThirdParty') {
            return thirdPartyAnswerKeys.includes(THIRD_PARTY_OPTION_SOMEONE_ELSE);
          }
          if (condition.func === 'isPatientSelf') {
            return thirdPartyAnswerKeys.includes(THIRD_PARTY_OPTION_MYSELF);
          }
          return true;
        }

        if (category === 'age-range') {
          return true;
        }

        return false;
      };

      const andConditions = eligibleConditions.filter((cond) => cond.logicType === 'AND');
      const orConditions = eligibleConditions.filter(
        (cond) => cond.logicType === 'OR' || !cond.logicType,
      );

      const andMet =
        andConditions.length === 0 || andConditions.every((cond) => evaluateCondition(cond));
      const orMet =
        orConditions.length === 0 || orConditions.some((cond) => evaluateCondition(cond));

      return andMet && orMet;
    },
    [answerMap, extractAnswerKeys, extractNumericValue, filterEligibleConditions, questionDetailsMap],
  );

  const questions = useMemo(
    () => allQuestions.filter((question) => isQuestionVisible(question)),
    [allQuestions, isQuestionVisible],
  );

  const questionCount = questions.length;

  const bodySelectTreeKeys = useMemo(() => {
    const keys = new Set<string>();

    questions.forEach((question: QuestionResponseDto) => {
      const { inputType, answerKeys } = question;
      if (inputType !== 'body_select' && inputType !== 'body_select_whole') return;
      const [treeKey] = answerKeys || [];
      if (treeKey) {
        keys.add(treeKey);
      }
    });
    return Array.from(keys);
  }, [questions]);

  const translationKeys = useMemo(() => {
    if (translationTreeKeys.length > 0) {
      return translationTreeKeys;
    }
    return bodySelectTreeKeys;
  }, [translationTreeKeys, bodySelectTreeKeys]);

  // Body Part 번역 데이터 로드 (useTranslations hook이 사용할 수 있도록 PaDataRepoState에 설정)
  useBodyPartTranslations({
    surveyId,
    treeKeys: translationKeys,
    locale: currentLocale,
    enabled: !!surveyId && translationKeys.length > 0,
  });

  // EQ-VAS 번역 데이터 로드
  const eqVasQuestionKeys = useMemo(() => {
    return questions
      .filter((q) => q.inputType === 'eq_vas')
      .map((q) => q.questionKey);
  }, [questions]);

  useEqVasTranslations({
    surveyId,
    eqVasQuestionKeys,
    locale: currentLocale,
    enabled: !!surveyId && eqVasQuestionKeys.length > 0,
  });

  // Range 번역 데이터 로드 (scale labels)
  const rangeQuestions = useMemo(() => {
    return questions
      .filter((q) => q.inputType === 'range')
      .map((q) => ({
        questionKey: q.questionKey,
        scaleLabels: q.typeOptions?.scaleLabels || [],
      }));
  }, [questions]);

  useRangeTranslations({
    surveyId,
    rangeQuestions,
    locale: currentLocale,
    enabled: !!surveyId && rangeQuestions.length > 0,
  });

  const bodySelectTreeConfigQueries = useQueries({
    queries: bodySelectTreeKeys.map((treeKey) => {
      const params = {
        surveyId: Number(surveyId || 0),
        locale: currentLocale || 'english',
        treeKey,
      } as any;
      const queryOptions = getQuestionControllerGetBodySelectTreeConfigQueryOptions(params);

      return {
        ...queryOptions,
        enabled: !!surveyId && !!currentLocale && !!treeKey,
      };
    }),
  });

  const bodySelectTreeInfoQueries = useQueries({
    queries: bodySelectTreeKeys.map((treeKey) => {
      const params = {
        surveyId: Number(surveyId || 0),
        treeKey,
      };

      return {
        ...getQuestionTreeControllerFindByTreeKeyQueryOptions(params),
        enabled: !!surveyId && !!treeKey,
      };
    }),
  });

  const bodySelectTreeQuestionQueries = useQueries({
    queries: bodySelectTreeKeys.map((treeKey, index) => {
      const treeInfo = bodySelectTreeInfoQueries[index]?.data as { id?: number } | undefined;
      const treeId = Number(treeInfo?.id || 0);

      return {
        ...getQuestionControllerFindByTreeIdQueryOptions(
          treeId || 0,
          { locale: currentLocale || 'english' },
        ),
        enabled: treeId > 0,
      };
    }),
  });

  const bodySelectTreeConfigMap = useMemo(() => {
    const map: Record<string, BodySelectTreeConfigResponseDto | undefined> = {};
    bodySelectTreeKeys.forEach((treeKey, index) => {
      map[treeKey] = bodySelectTreeConfigQueries[index]?.data as BodySelectTreeConfigResponseDto | undefined;
    });
    return map;
  }, [bodySelectTreeKeys, bodySelectTreeConfigQueries]);

  const bodySelectTreeConfigLoadingMap = useMemo(() => {
    const map: Record<string, boolean> = {};
    bodySelectTreeKeys.forEach((treeKey, index) => {
      map[treeKey] = Boolean(bodySelectTreeConfigQueries[index]?.isLoading);
    });
    return map;
  }, [bodySelectTreeKeys, bodySelectTreeConfigQueries]);

  const bodySelectTreeFollowUpQuestionsMap = useMemo(() => {
    const map: Record<string, QuestionResponseDto[]> = {};

    bodySelectTreeKeys.forEach((treeKey, index) => {
      const config = bodySelectTreeConfigMap[treeKey];
      const entryQuestionKey = config?.entryQuestionKey || config?.entryQuestion?.questionKey;
      const treeQuestions = (bodySelectTreeQuestionQueries[index]?.data || []) as QuestionResponseDto[];

      map[treeKey] = treeQuestions.filter((question) => {
        if (entryQuestionKey && question.questionKey === entryQuestionKey) {
          return false;
        }
        return question.enabled !== false;
      });
    });

    return map;
  }, [bodySelectTreeConfigMap, bodySelectTreeKeys, bodySelectTreeQuestionQueries]);

  const bodySelectTreeReadyMap = useMemo(() => {
    const map: Record<string, boolean> = {};

    bodySelectTreeKeys.forEach((treeKey, index) => {
      const treeInfo = bodySelectTreeInfoQueries[index]?.data as { id?: number } | undefined;
      const treeQuestions = (bodySelectTreeQuestionQueries[index]?.data || []) as QuestionResponseDto[];
      const entryQuestionKey =
        bodySelectTreeConfigMap[treeKey]?.entryQuestionKey
        || bodySelectTreeConfigMap[treeKey]?.entryQuestion?.questionKey;

      const hasTreeQuestions = treeQuestions.length > 0;
      const hasEntryQuestion =
        !!entryQuestionKey
        && treeQuestions.some((question) => question.questionKey === entryQuestionKey);

      map[treeKey] = Boolean(treeInfo) && hasTreeQuestions && hasEntryQuestion;
    });

    return map;
  }, [bodySelectTreeConfigMap, bodySelectTreeInfoQueries, bodySelectTreeKeys, bodySelectTreeQuestionQueries]);

  // 전화번호 인증 상태 관리 (Live Testing용 Mock 인증)
  const phoneVerification = usePhoneVerification({
    treeKey: selectedTreeKey,
  });

  // 전체 로딩 상태
  const isLoading = isContextLoading || isQuestionsLoading;

  return {
    // Tree 정보
    selectedTreeId,
    selectedTreeKey,
    title,
    description,
    showHeader,
    currentLocale,
    visitUuid,

    // Survey/Org 정보 (sx_select 트리 검색용)
    surveyId,
    organizationId,

    // 질문 데이터
    questions,
    allQuestions, // 필터링되지 않은 모든 질문 (sx_collect_associated용)
    questionCount,
    isLoading,
    isError,
    error,

    // 답변 상태
    answerMap,
    updateAnswer,
    resetAnswers,
    getAnswer,
    hasAnswer,

    // 성별 (body selector 이미지용)
    isMale,

    // 전화번호 인증 (Live Testing용 Mock)
    phoneVerification,

    // body_select tree config
    bodySelectTreeConfigMap,
    bodySelectTreeConfigLoadingMap,
    bodySelectTreeFollowUpQuestionsMap,
    bodySelectTreeReadyMap,

    // Stage Milemarker용 stages
    stageMarkerStages,

    // Empty states
    emptySelectionLabel,
    emptySelectionDescription,
    emptyQuestionsLabel,
    emptyQuestionsDescription,

    // sx_collect_associated용 선택된 증상 트리 키들
    selectedSymptomTreeKeys: computedSelectedSymptomTreeKeys,

    // 질문 스킵 콜백 (sx_collect_associated 등에서 사용)
    onSkipQuestion,
    reportState,
    survey,

    // sx_collect_intelligent auto suggestion
    suggestSymptoms,
    isSuggestingSymptoms: suggestSymptomsMutation.isPending,
  };
});

export default LiveTestingContainer;
